import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { TableShapeRenderer } from './TableShapeRenderer'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import type { Table, TableState, SeatLayout } from '@/lib/types/table'
import type { NearbyInfo } from './FloorPlanCanvas'

interface DraggableTableProps {
  table: Table
  state: TableState
  guestName?: string
  areaId: string
  combineInfo?: NearbyInfo
  overridePos?: { x: number; y: number }
  posOrderStatus?: 'none' | 'open' | 'in_progress' | 'paid'
  onPOSTableClick?: (tableDocumentId: string) => void
  /** When true, disables all pointer interaction (used for tables inside a combination overlay) */
  disableInteraction?: boolean
  /** When true, suppresses all text labels (used for tables inside a combination overlay) */
  hideLabel?: boolean
}

export function DraggableTable({ table, state, guestName, areaId, combineInfo, overridePos, posOrderStatus, onPOSTableClick, disableInteraction, hideLabel }: DraggableTableProps) {
  const { positions, selectedTableId, selectTable, mode, rejectedTableIds } = useFloorPlanStore()
  const isRejected = rejectedTableIds.has(String(table.id))
  const pos = overridePos ?? positions[String(table.id)] ?? { x: 40, y: 40 }
  const isSelected = selectedTableId === String(table.id)

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(table.id),
    data: { areaId },
    disabled: mode === 'live' || mode === 'pos' || overridePos !== undefined,
  })

  const style: React.CSSProperties = {
    position: 'absolute',
    left: pos.x,
    top: pos.y,
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : isSelected ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
    transition: isDragging ? 'none' : 'opacity 0.2s',
    pointerEvents: disableInteraction ? 'none' : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-table-node
      onClick={(e) => {
        e.stopPropagation()
        if (mode === 'pos' && onPOSTableClick) {
          onPOSTableClick(table.documentId)
          return
        }
        selectTable(isSelected ? null : String(table.id))
      }}
    >
      {combineInfo && (
        <div
          className={`absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded-full text-[9px] font-semibold z-10 ${
            combineInfo.glow === 'green'
              ? 'bg-green-500/20 text-green-400 border border-green-400/30'
              : 'bg-red-500/20 text-red-400 border border-red-400/30'
          }`}
        >
          {combineInfo.glow === 'green' ? combineInfo.names.join('+') : '⊘ Not combinable'}
        </div>
      )}
      <TableShapeRenderer
        name={table.name}
        seatsMax={table.seatsMax}
        shape={table.shape}
        state={state}
        guestName={guestName}
        isSelected={isSelected}
        combineGlow={combineInfo?.glow ?? null}
        isRejected={isRejected}
        posOrderStatus={posOrderStatus}
        seatLayout={(table.metadata as any)?.seatLayout as SeatLayout | undefined}
        hideLabel={hideLabel}
      />
    </div>
  )
}
